import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { CommandCenter } from './pages/CommandCenter';
import { LeadDetail } from './pages/LeadDetail';
import { WhatsAppCockpit } from './pages/WhatsAppCockpit';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Debug } from './pages/Debug';
import { CRMDashboard } from './pages/CRMDashboard';
import { ToastContainer } from './components/Toast';
import { initializeLanguage, getLanguage } from './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5000, retry: 1 },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RTLWrapper({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initializeLanguage();
    const lang = getLanguage();
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, []);
  
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RTLWrapper>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/debug" element={<ProtectedRoute><Debug /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/crm" replace />} />
              <Route path="crm" element={<CRMDashboard />} />
              <Route path="command-center" element={<CommandCenter />} />
              <Route path="leads/:leadId" element={<LeadDetail />} />
              <Route path="whatsapp" element={<WhatsAppCockpit />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ToastContainer />
        </BrowserRouter>
      </RTLWrapper>
    </QueryClientProvider>
  );
}
